import { Alert, AlertTitle, Divider, Typography } from '@mui/material';
import Button from 'components/Button';
import FormField from 'further-ui/components/FormField';
import ConsiderationTotals from '../ConsiderationTotals';
import PageLoader from 'components/PageLoader';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal/index.tsx';
import RealisationsPage from '../RealisationsPage';
import { Steps } from '../steps';
import { Column, Row } from 'further-ui/components/Layout';
import { Transaction } from 'components/TransactionAllocator';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import {
  ConsiderationType,
  ConsiderationTypeHoldingClassTypes,
} from 'further-types/lp/realisation';
import { Vehicle } from 'further-types/lp/vehicle';
import { useDisclosure } from 'further-ui/hooks';
import { CalendarDay, numberToDisplayString } from 'further-ui/utils';
import { realisation as rules } from 'further-ui/validations';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useCurrencyRate from 'hooks/lp/data/currencies/useCurrencyRate';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation.ts';
import api from 'lib/trpcClient.ts';
import { compact, sumBy } from 'lodash';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { RealisationFormState } from '../types';
import buildRealisationPayload from './helpers/buildRealisationPayload.ts';
import ReviewAccountTransactions from './ReviewAccountTransactions.tsx';
import { realisations } from 'further-ui/domain';
import useSummaryData from './useSummaryData.ts';
import PerClassAllocationTable from '../AllocationTables/PerClassAllocationTable/index.tsx';
import PerInvestorAllocationTable from '../AllocationTables/PerInvestorAllocationTable/index.tsx';
import CashConsiderationsTable from './CashConsiderationsTable.tsx';

type SummaryProps = {
  onSuccess: () => void;
  onPrevious: () => void;
  selectedVehicle: Vehicle;
};

const Summary: React.FC<SummaryProps> = ({
  onSuccess,
  onPrevious,
  selectedVehicle,
}) => {
  const { getValues, setValue } =
    useFormContext<rules.CreateRealisationFormValues>();
  const { hasEqualisationInDraftState } = useEqualisations(selectedVehicle._id);
  const confirmDialog = useDisclosure();

  const mutationOptions = useHandleTrpcMutation()({
    onSuccess: () => {
      onSuccess();
    },
    successMessage: 'Realisation created successfully.',
    refetchType: 'none', // Invalidate but don't refetch
  });

  const holdingClassRealisation = getValues('holdingClassRealisation');

  const holdingClassInvestmentsWithUnitSold = holdingClassRealisation.flatMap(
    (holdingClass) => holdingClass.unitsAllocationPerInvestment ?? [],
  );

  const currentRealisationPerHoldingClassInvestment = compact(
    holdingClassRealisation.flatMap((holdingClass) =>
      holdingClass.unitsAllocationPerInvestment?.map((investment) => ({
        investmentId: investment.investmentId,
        holdingClassId: holdingClass.holdingClassId,
        unitsSold: investment.allocatedUnits ?? 0,
      })),
    ),
  );

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useSummaryData({
    vehicleId: selectedVehicle._id,
    holdingClassInvestmentsWithUnitSold,
    assetId: getValues('assetId'),
    realisationDate: getValues('ledgerDate'),
    currentRealisationPerHoldingClassInvestment,
  });

  const { state } = useFormWithStateContext<
    rules.CreateRealisationFormValues,
    RealisationFormState
  >();

  const ledgerDate = getValues('ledgerDate') ?? new CalendarDay(new Date());

  const assetName = getValues('assetName');

  const considerations = getValues('consideration');

  const cashConsiderationIndex = considerations.findIndex(
    (consideration) =>
      consideration.considerationType ===
      ConsiderationType.CashAndWithholdingTax,
  );

  const holdingClassInvestmentsConsiderationValues =
    state.consideration[cashConsiderationIndex]
      ?.holdingClassInvestmentsConsiderationValues;

  const investorAllocationsTotals =
    state.consideration[cashConsiderationIndex]?.investorAllocationsTotals;

  const considerationCurrency =
    considerations[cashConsiderationIndex]?.currency ??
    selectedVehicle.baseCurrency;

  const holdingClassCurrencies = holdingClassRealisation.flatMap(
    (holdingClass) =>
      holdingClass.unitsAllocationPerInvestment?.map(
        (investment) => investment.currency,
      ),
  );

  const holdingClassCurrency = holdingClassCurrencies[0];

  const isHoldingClassCurrencyDifferent = !holdingClassCurrencies.every(
    (currency) => currency === holdingClassCurrency,
  );

  if (isHoldingClassCurrencyDifferent && !holdingClassCurrency) {
    throw new Error('Holding class currencies are different.');
  }

  const realisationType = realisations.getRealisationType({
    holdingClassInvestment: {
      currency: holdingClassCurrency ?? considerationCurrency,
    },
    vehicleCurrencyCode: selectedVehicle.baseCurrency,
    realisationCurrencyCode: considerationCurrency,
  });

  const { mutateAsync: createRealisation, isPending } =
    api.lpRealisation.create.useMutation(mutationOptions);

  const handleSubmit = async () => {
    const cashConsideration = getValues('consideration').find(
      (consideration) =>
        consideration.considerationType ===
        ConsiderationType.CashAndWithholdingTax,
    );

    if (!cashConsideration || !holdingClassInvestmentsConsiderationValues) {
      throw new Error('Cash consideration not found.');
    }

    const payload = buildRealisationPayload({
      selectedVehicle,
      assetId: getValues('assetId'),
      ledgerDate: getValues('ledgerDate'),
      currencyRate: getValues('currencyRate'),
      holdingClassRealisation,
      consideration: getValues('consideration'),
      cashConsideration,
      finalTransactions: getValues('transactions') ?? [],
      holdingClassInvestmentConsiderationValues:
        holdingClassInvestmentsConsiderationValues,
    });

    await createRealisation(payload);
  };

  const currencyForTcyDisplay =
    realisationType ===
    ConsiderationTypeHoldingClassTypes.LcyConsiderationForNonLcyHoldingClass
      ? holdingClassCurrency ?? considerationCurrency
      : considerationCurrency;

  const isThreeCyFxRateRealisation =
    realisationType ===
    ConsiderationTypeHoldingClassTypes.ThreeCYNonLcyConsiderationNonLcyHoldingClass;

  const {
    fxRate: threeCyFxRate,
    changeRate: threeCyChangeRate,
    isFetchingRate: threeCyIsFetchingRate,
  } = useCurrencyRate({
    vehicleId: selectedVehicle._id,
    currency: holdingClassCurrency ?? considerationCurrency,
    date: ledgerDate,
    defaultCurrencyRate: getValues('threeCyCurrencyRate') ?? null,
    onRateChange: (rate) => {
      setValue('threeCyCurrencyRate', rate ?? undefined);
    },
  });

  const { fxRate, changeRate, isFetchingRate } = useCurrencyRate({
    vehicleId: selectedVehicle._id,
    currency: currencyForTcyDisplay,
    date: ledgerDate,
    defaultCurrencyRate: getValues('currencyRate') ?? null,
    onRateChange: (rate) => {
      setValue('currencyRate', rate ?? undefined);
    },
  });

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const onChangeTransaction = (transactions: Array<Transaction>) => {
    setValue('transactions', transactions);
  };

  if (isSummaryError) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        Failed to load realisation summary data.
      </Alert>
    );
  }

  if (
    transactionTypesLoading ||
    !holdingClassInvestmentsConsiderationValues ||
    isSummaryLoading ||
    !summaryData
  ) {
    return <PageLoader />;
  }

  const { unrealisedAssetFXGainsAndLossesUnwinds } = summaryData;

  return (
    <RealisationsPage
      activeStep={Steps.Summary}
      actionBar={
        <Row fullWidth justify="space-between">
          <Button size="large" variant="outlined" onClick={onPrevious}>
            Previous step
          </Button>
          <Row spacing="sm">
            <Button
              variant="contained"
              color="primary"
              size="large"
              loading={isPending}
              onClick={
                hasEqualisationInDraftState
                  ? confirmDialog.onOpen
                  : handleSubmit
              }
            >
              Save and approve
            </Button>
          </Row>
        </Row>
      }
    >
      <FormField label="Vehicle" labelColorLight>
        <Typography variant="h6">{selectedVehicle.vehicleName}</Typography>
      </FormField>
      <FormField label="Realised asset" labelColorLight>
        <Typography variant="h6">{assetName}</Typography>
      </FormField>
      <FormField label="Realisation date" labelColorLight>
        <Typography variant="h6">
          {getValues('ledgerDate')?.toReadable()}
        </Typography>
      </FormField>
      <FormField label="Classes realised" labelColorLight>
        <Typography variant="h6">
          {holdingClassRealisation
            .map((holdingClass) => holdingClass.name)
            .join(', ')}
        </Typography>
      </FormField>
      <FormField label="Total units realised" labelColorLight>
        <Typography variant="h6">
          {numberToDisplayString(sumBy(holdingClassRealisation, 'unitsSold'))}
        </Typography>
      </FormField>
      <CashConsiderationsTable
        currency={considerationCurrency}
        holdingClassRealisations={holdingClassRealisation}
      />
      <Divider />
      <Column spacing="sm">
        <Typography variant="h5">Per class allocation</Typography>
        <PerClassAllocationTable
          holdingClassRealisations={holdingClassRealisation}
          currency={considerationCurrency}
        />
      </Column>
      <Divider />
      <Column spacing="sm">
        <Typography variant="h5">Per investor allocation</Typography>
        <PerInvestorAllocationTable
          currency={considerationCurrency}
          investorAllocationsTotals={investorAllocationsTotals}
        />
      </Column>
      <Divider />
      <Column spacing="sm">
        <Typography variant="h5">Review account transactions</Typography>
        <ReviewAccountTransactions
          selectedVehicle={selectedVehicle}
          currency={considerationCurrency}
          currencyForTcyDisplay={currencyForTcyDisplay}
          holdingClassCurrency={holdingClassCurrency ?? considerationCurrency}
          ledgerDate={ledgerDate}
          fxRate={fxRate}
          changeRate={changeRate}
          isFetchingRate={isFetchingRate}
          isThreeCyFxRateRealisation={isThreeCyFxRateRealisation}
          threeCyFxRate={threeCyFxRate}
          threeCyChangeRate={threeCyChangeRate}
          threeCyIsFetchingRate={threeCyIsFetchingRate}
          transactionTypeOptions={transactionTypeOptions}
          onChangeTransaction={onChangeTransaction}
          assetId={getValues('assetId')}
          unrealisedAssetFXGainsAndLossesUnwinds={
            unrealisedAssetFXGainsAndLossesUnwinds
          }
          holdingClassRealisation={holdingClassRealisation}
        />
      </Column>
      <Divider />
      <Column spacing="sm">
        <Typography variant="h5">Realisation summary</Typography>
        <ConsiderationTotals
          considerations={considerations}
          currency={considerationCurrency}
        />
        <Typography variant="body2">
          Earnout inputs are for record-keeping only and will not be added to
          the General ledger.
        </Typography>
      </Column>

      {hasEqualisationInDraftState && (
        <PendingDraftEqualisationsModal
          isOpen={confirmDialog.isOpen}
          onClose={confirmDialog.onClose}
          onConfirm={handleSubmit}
          loading={isPending}
        />
      )}
    </RealisationsPage>
  );
};

export default Summary;
